import { observe } from "mobx";

import { autobind, debounce } from "@use/decorators";

import { Model } from "./Model";

/**
 * Specification of observer callbacks, which will be triggered whenever
 * internal data on the model changes.
 */
type Observer = () => void;

/**
 * We're using this symbol to safely store the list of observers subscribed to
 * model instances without risking namespace collisions.
 */
const observers = Symbol("ObservableModel.observers");

/**
 * We're using this symbol to safely store the disposer returned from mobx, when
 * we began observing the model for changes.
 */
const observeDisposer = Symbol("ObservableModel.observeDisposer");

/**
 * An observable model provides a mechanism to subscribe to data changes by
 * using mobx to observe changes to observable properties on model instances.
 */
export abstract class ObservableModel extends Model {
  /**
   * Contains the list of currently applied observers, which will be notified
   * about any changes in our data model.
   */
  private [observers]: Set<Observer> = new Set();

  /**
   * Contains a reference to the dispose method returned by mobx, when we
   * began observing the model for changes (used to unobserve the model later
   * on).
   */
  private [observeDisposer]?: () => void;

  /**
   * Publicly expose a variable, which we can use to determine if the model
   * is currently being observed.
   */
  public get isObserved(): boolean {
    return !!this[observeDisposer];
  }

  /**
   * Helper that'll subscribe the given callback to data change events, so
   * that it can easily interact to any changes made.
   */
  public observe(callback: Observer): void {
    // If we're not already observing the model, then begin doing so now
    if (!this[observeDisposer]) {
      this[observeDisposer] = observe(this, this.emitChange);
    }

    // Subscribe the callback to change events
    this[observers].add(callback);
  }

  /**
   * Helper that'll unsubscribe the given callback from data change events.
   */
  public unobserve(callback: Observer): void {
    this[observers].delete(callback);

    // If there are no observers left, then dispose of the observer
    const disposer = this[observeDisposer];

    if (this[observers].size === 0 && disposer) {
      // Dispose observation
      disposer();

      // Remove the disposer internally
      this[observeDisposer] = undefined;
    }
  }

  /**
   * Internal helper that's used to actually emit changes
   */
  @autobind()
  @debounce(1)
  private emitChange(): void {
    for (const observer of this[observers]) {
      observer();
    }
  }
}
